import { useState, useEffect } from 'react';
import { Modal } from './ui';

const EditGroupModal = ({ isOpen, onClose, group, onSave }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    group_type: 'public',
    industry_tags: [],
    location: '',
    rules: ['']
  });
  const [loading, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const industryOptions = [
    'Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Agriculture',
    'Education', 'Retail', 'Construction', 'Transportation', 'Energy',
    'Media', 'Real Estate', 'Professional Services', 'Government',
    'Non-profit', 'Food & Beverage', 'Tourism', 'Automotive', 'Textile'
  ];

  useEffect(() => {
    if (group) {
      setFormData({
        id: group.id,
        name: group.name || '',
        description: group.description || '',
        group_type: group.group_type || 'public',
        industry_tags: group.industry_tags || [],
        location: group.location || '',
        rules: group.rules && group.rules.length > 0 ? group.rules : ['']
      });
    }
  }, [group]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = () => {
    const newTag = tagInput.trim();
    if (newTag && !formData.industry_tags.includes(newTag)) {
      setFormData(prev => ({
        ...prev,
        industry_tags: [...prev.industry_tags, newTag]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      industry_tags: prev.industry_tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleRuleChange = (index, value) => {
    const newRules = [...formData.rules];
    newRules[index] = value;
    setFormData(prev => ({
      ...prev,
      rules: newRules
    }));
  };

  const addRule = () => {
    setFormData(prev => ({
      ...prev,
      rules: [...prev.rules, '']
    }));
  };

  const removeRule = (index) => {
    if (formData.rules.length > 1) {
      const newRules = formData.rules.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        rules: newRules
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      alert('Group name is required');
      return;
    }
    if (formData.name.trim().length < 3) {
      alert('Group name must be at least 3 characters');
      return;
    }
    if (!formData.description.trim()) {
      alert('Group description is required');
      return;
    }
    if (formData.description.trim().length < 10) {
      alert('Group description must be at least 10 characters');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving group:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      group_type: 'public',
      industry_tags: [],
      location: '',
      rules: ['']
    });
    setTagInput('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} maxWidth="2xl">
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="text-2xl mr-3">✏️</span>
          Edit Group: {group?.name}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Group Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
              Group Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Enter group name"
              required
              minLength={3}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
              placeholder="Describe what your group is about..."
              required
              minLength={10}
            />
          </div>

          {/* Group Type */}
          <div>
            <label htmlFor="group_type" className="block text-sm font-semibold text-gray-700 mb-2">
              Group Type
            </label>
            <select
              id="group_type"
              value={formData.group_type}
              onChange={(e) => handleInputChange('group_type', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="public">🌐 Public - Anyone can find and join</option>
              <option value="private">🔒 Private - Invite only</option>
            </select>
          </div>

          {/* Industry Tags */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Industry Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.industry_tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm flex items-center space-x-1"
                >
                  <span>#{tag}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-teal-600 hover:text-teal-800 ml-1"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Add industry tag..."
                list="industry-options"
              />
              <datalist id="industry-options">
                {industryOptions.map((option, index) => (
                  <option key={index} value={option} />
                ))}
              </datalist>
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Add
              </button>
            </div>
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
              Location (Optional)
            </label>
            <input
              type="text"
              id="location"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="e.g., Jakarta, Indonesia"
            />
          </div>

          {/* Rules */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Group Rules & Guidelines
            </label>
            {formData.rules.map((rule, index) => (
              <div key={index} className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={rule}
                  onChange={(e) => handleRuleChange(index, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder={`Rule ${index + 1}...`}
                />
                {formData.rules.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRule(index)}
                    className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addRule}
              className="text-sm text-teal-600 hover:text-teal-700 flex items-center space-x-1"
            >
              <span>+</span>
              <span>Add another rule</span>
            </button>
          </div>

          {/* Actions */}
          <div className="flex space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>💾</span>
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditGroupModal;